import * as React from 'react';
import { Progress } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getClientDuplicatesAsync } from '../../../ajax/requests';
import { DuplicateGroup, DuplicateOptions } from './types';
import StyledTable from '../../../components/StyledTable';

interface Props {
    options: DuplicateOptions;
    onComplete: (groups: DuplicateGroup[]) => void;
}

const FetchStep: React.FC<Props> = ({ options, onComplete }) => {
    const t = useTranslations();
    const [loading, setLoading] = React.useState(true);
    const [groups, setGroups] = React.useState<DuplicateGroup[] | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setGroups(null);
        setError(null);

        getClientDuplicatesAsync(options.field)
            .then(result => {
                if (cancelled) return;
                setGroups(result);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                setError(err?.message ?? t(TK.somethingWentWrong));
                setLoading(false);
            });

        return () => { cancelled = true; };
    }, [options.field]);

    const totalToDelete = React.useMemo(
        () => groups?.reduce((sum, g) => sum + g.clients.length - 1, 0) ?? 0,
        [groups],
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)', alignItems: 'center', padding: 'var(--spacer-L) 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t(TK.clearDuplicatesFetching)}</span>
                <Progress animated value={100} style={{ width: '100%', height: '8px' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ color: 'var(--danger-color)', padding: 'var(--spacer-S)' }}>{error}</div>
        );
    }

    if (!groups || groups.length === 0) {
        return (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--spacer-L) 0', fontSize: '1rem' }}>
                {t(TK.clearDuplicatesNoResult)}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <div style={{ display: 'flex', gap: 'var(--spacer-M)', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                <span>
                    <strong style={{ color: 'var(--danger-color)' }}>{groups.length}</strong>{' '}
                    {t(TK.clearDuplicatesGroupsFound)}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                    (~<strong>{totalToDelete}</strong> {t(TK.clearDuplicatesWillDelete)})
                </span>
            </div>
            <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <StyledTable hover size='sm'>
                    <thead>
                        <tr>
                            <th>{t(TK.value)}</th>
                            <th style={{ textAlign: 'right' }}>{t(TK.clients)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map(g => (
                            <tr key={g.fieldValue}>
                                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{g.fieldValue}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <span style={{ color: 'var(--danger-color)', fontWeight: 600 }}>{g.clients.length}</span>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: 'var(--spacer-XS)' }}>
                                        ({g.clients.map(c => c.name).join(', ')})
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                    onClick={() => onComplete(groups)}
                    style={{
                        padding: 'var(--spacer-XS) var(--spacer-S)',
                        borderRadius: '4px',
                        border: 'none',
                        background: 'var(--appGreen)',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                    }}
                >
                    {t(TK.next)}
                </button>
            </div>
        </div>
    );
};

export default FetchStep;
